import { useCookies } from '@/app/cookie-provider';
import BadgeStoreFront from '@/components/badge-storefront';
import { ElementProps } from '@/components/sections/builder/types';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { dummyCarts } from '@/constant/dummyData/dummy-carts';
import { deleteCartItem, updateCartItem } from '@/lib/actions/cart/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { cn, debounce, formatIDR, formatNumber } from '@/lib/utils';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import { HttpTypes } from '@/types';
import { StoreCart } from '@/types/http';
import { useQueryClient } from '@tanstack/react-query';
import { CircleAlert, MinusIcon, PlusIcon, Trash2 } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { shallow } from 'zustand/shallow';

export default function ElementContent({
  data,
  host,
  preview,
  theme,
}: ElementProps<HttpTypes.StoreCart>) {
  const { multiOrigin } = useCookies();
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
  const [currentItemQuantities, setCurrentItemQuantities] = useState<{ [key: string]: number }>({});
  const queryClient = useQueryClient();

  const carts = preview ? dummyCarts : data;
  const cartItems = carts?.items || [];
  const isEmptyCart = cartItems.length === 0;

  const builder = useBuilderStore((state) => state, shallow);
  const themeContent = preview ? builder.theme : theme;

  const debouncedIncrementQty = useCallback(
    debounce(async (item: StoreCart, qty: number) => {
      setIsLoading((prev) => ({ ...prev, [item.id]: true }));
      await updateCartItem(host as string, { ...item, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    }, 500),
    []
  );

  const debouncedDecrementQty = useCallback(
    debounce(async (item: StoreCart, qty: number) => {
      setIsLoading((prev) => ({ ...prev, [item.id]: true }));
      await updateCartItem(host as string, { ...item, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    }, 500),
    []
  );
  const incrementQty = (item: StoreCart, idx: number = 0) => {
    const newQty = (currentItemQuantities[item.id] || item.quantity) + 1;
    if (newQty > (data?.item_stock?.[idx]?.stock || 0)) return;
    setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: newQty }));
    debouncedIncrementQty(item, newQty);
  };

  const handleDecrement = (item: StoreCart) => {
    const newQty = Math.max(1, (currentItemQuantities[item.id] || item.quantity) - 1);
    setCurrentItemQuantities((prev) => ({ ...prev, [item.id]: newQty }));
    debouncedDecrementQty(item, newQty);
  };

  const removeItem = async (item: StoreCart) => {
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));
    await deleteCartItem(host as string, item.id);
    queryClient.invalidateQueries({ queryKey: ['carts'] });

    SendEventAnalytic('remove_from_cart', {
      currency: 'IDR',
      value: Number(item.sell_price) * item.quantity,
      items: [
        {
          item_name: item?.product_name,
          item_id: item.id as string,
          price: Number(item?.sell_price),
          quantity: item.quantity,
          unique_action_id: Date.now(),
        },
      ],
    });

    SendEventMetapixel('RemoveFromCart', {
      content_ids: [item.id],
      content_name: item.product_name,
      content_type: 'product',
      quantity: item.quantity,
      value: Number(item.sell_price) * item.quantity,
      currency: 'IDR',
    });

    setIsLoading((prev) => ({ ...prev, [item.id]: false }));
  };

  const handleOutOfStock = (idx: number) => {
    return data?.item_stock?.[idx]?.stock === 0;
  };

  const checkStockAfterChangeOrigin = (idx: number, quantity: number): boolean => {
    const stockValue = data?.item_stock?.[idx]?.stock;
    const stock = Number(stockValue) || 0;
    return quantity > stock;
  };

  const handleDisableAction = (idx: number, quantity: number): boolean => {
    const stockValue = data?.item_stock?.[idx]?.stock;
    const stock = Number(stockValue) || 0;
    return quantity >= stock;
  };
  return (
    <>
      <section className='theme_cart_page_bg'>
        <div className='theme_layout_page_width mx-auto px-6 duration-100'>
          {isEmptyCart ? (
            <div className='mb-4 flex justify-center'>
              <h1 className='theme_cart_page_color text-lg md:text-3xl'>Your cart is empty</h1>
            </div>
          ) : (
            <div className='theme_cart_page_color mb-4 flex items-center justify-between'>
              <h1 className='text-lg md:text-3xl'>Your cart</h1>
              <Link href='/' className='text-sm underline md:text-base'>
                {themeContent?.cart?.labelContinueShopping || 'Continue shopping'}
              </Link>
            </div>
          )}
          <div>
            {!isEmptyCart && (
              <div className='theme_cart_page_color hidden grid-cols-6 border-b py-4 uppercase md:grid'>
                <div className='col-span-4'>Product</div>
                <div>Quantity</div>
                <div className='text-right'>Total</div>
              </div>
            )}
            {cartItems.map((item, idx) => {
              const images = (item.product_assets || []).filter(
                (asset) => asset.assets_type === 'image'
              );
              const displayedQuantity = currentItemQuantities[item.id] || item.quantity;
              const totalPrice =
                displayedQuantity * (item.discount_price ? item.discount_price : item.sell_price);
              const isOutOfStock = handleOutOfStock(idx || 0);
              const disable = handleDisableAction(idx || 0, item.quantity);
              const showNotifyStock = checkStockAfterChangeOrigin(idx || 0, item.quantity);
              const stock = Number(data?.item_stock?.[idx]?.stock);
              const suggestionStock = (idx: number) => {
                return data?.item_stock?.[idx]?.available_stock || [];
              };

              return (
                <div
                  className='grid-cols-1 items-center border-b py-6 md:grid md:grid-cols-6'
                  key={item.id}
                >
                  <div className='md:col-span-4'>
                    <div className='flex gap-x-6'>
                      <div>
                        <Image
                          src={images[0]?.assets_url}
                          alt={item.product_name}
                          width={120}
                          height={120}
                          className={cn('aspect-square border object-contain', {
                            '!grayscale': isOutOfStock,
                          })}
                        />
                      </div>

                      <div className='flex-col space-y-1 w-full max-w-full overflow-hidden px-4'>
                        <div className={cn('w-fit', { '!grayscale': isOutOfStock })}>
                          <BadgeStoreFront theme={theme as ThemeType} item={item} />
                        </div>
                        <div className='flex flex-col'>
                          <h2
                            className={cn(
                              'theme_cart_page_color text-base truncate break-all md:break-words',
                              isOutOfStock && 'text-gray-500'
                            )}
                          >
                            {item.product_name}
                          </h2>
                          {item.variants?.map((variant) => (
                            <p className="truncate break-all md:break-words" key={variant.option_id}>{variant.label}</p>
                          ))}
                        </div>
                        <div
                          className={cn(
                            item?.discount_price && 'flex flex-col items-start gap-1',
                            isOutOfStock && 'text-gray-500'
                          )}
                        >
                          <span
                            className={cn(
                              ' text-base font-bold',
                              isOutOfStock ? 'text-gray-500' : 'text_product_secondary'
                            )}
                          >
                            {formatIDR(
                              item?.discount_price ? item?.discount_price : item?.sell_price || 0
                            )}
                          </span>
                          {item?.discount_price ? (
                            <span className='text-sm text-gray-500 line-through'>
                              {formatIDR(item?.sell_price || 0)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='theme_cart_page_color mb-2 space-y-2 md:mb-0'>
                    <div className='flex flex-row-reverse gap-x-2 gap-y-2 md:flex-row'>
                      <div className='theme_cart_page_color flex h-10 w-fit items-center rounded border'>
                        <button
                          onClick={() => handleDecrement(item)}
                          className={`h-full px-3 ${
                            displayedQuantity <= 1 ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                          disabled={displayedQuantity <= 1}
                        >
                          <MinusIcon className='h-4 w-4 cursor-pointer ' />
                        </button>
                        <span className='mx-6'>{formatNumber(displayedQuantity)}</span>
                        <button
                          disabled={disable}
                          onClick={() => incrementQty(item, idx)}
                          className={cn('h-full px-3')}
                        >
                          <PlusIcon
                            className={cn(
                              'h-4 w-4  ',
                              disable ? 'cursor-not-allowed' : 'cursor-pointer'
                            )}
                          />
                        </button>
                      </div>
                      <button onClick={() => removeItem(item)} disabled={isLoading[item.id]}>
                        <Trash2 className='h-4 w-4' />
                      </button>
                      {multiOrigin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                            <CircleAlert className='h-4 w-4' />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='center' className='z-[1000] w-[200px] '>
                            <DropdownMenuLabel>See Where It’s Available</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                              {suggestionStock(idx).map((stockItem, index) => (
                                <DropdownMenuItem
                                  key={index}
                                  className='flex justify-between gap-x-3'
                                >
                                  <span>{stockItem.location_name}</span>
                                  <span>{stockItem.quantity}</span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    {multiOrigin && (
                      <div className='flex flex-row-reverse gap-x-2 gap-y-2 text-center md:w-full md:flex-row'>
                        {multiOrigin &&
                          ((stock === 0 && isOutOfStock) || (stock > 0 && showNotifyStock)) && (
                            <Badge variant='destructive'>
                              <span className='text-[10px]'>
                                {stock === 0 && isOutOfStock
                                  ? 'Stock Not Available'
                                  : 'Stock Is Not Enough'}
                              </span>
                            </Badge>
                          )}
                      </div>
                    )}
                  </div>

                  <div
                    className={cn(
                      'text-right text-sm md:text-base',
                      isOutOfStock ? 'text-gray-500' : 'theme_cart_page_color'
                    )}
                  >
                    {formatIDR(totalPrice)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* <style jsx></style> */}
    </>
  );
}
